import {
  LocationRead,
  LocationSettingsTranslationLocationReadTypeEnum,
} from '@/api';
import usePartnerStore from '@/composables/store/usePartnerStore';
import helpers from '@/services/helpers';
import { computed } from 'vue';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';
import useReservationStore from './useReservationStore';

export default function useLocationsStore() {
  const store = useStore();

  const locations = computed(() => store.state.locations.locations ?? []);
  const allLocations = computed(() => store.state.locations.allLocations ?? []);
  const locationsPromise = computed(
    () => store.state.locations.locationsPromise,
  );
  const fetchLocations = async () => {
    await store.dispatch('locations/fetchLocations');
  };

  const { locale } = useI18n({ useScope: 'global' });

  const locationById = (location: string | number | Record<string, any>) => {
    const locationId = helpers.getIRI(location, '/api/locations/');
    return allLocations.value.find(
      (loc) => helpers.getIRI(loc, '/api/locations/') === locationId,
    );
  };

  const { reservationLocation } = useReservationStore();

  const location = computed<LocationRead | undefined>(() => {
    return reservationLocation.value
      ? locationById(reservationLocation.value)
      : undefined;
  });

  const getTranslation = (
    type: LocationSettingsTranslationLocationReadTypeEnum,
  ) => {
    if (!location.value?.locationSettings?.translations) {
      return;
    }

    const filtered = {
      translations: location.value.locationSettings.translations.filter(
        (translation) => translation.type === type,
      ),
    };

    return helpers.getItemTranslation(filtered as any, locale.value);
  };

  const meetingRoomDisclaimer = computed(() =>
    getTranslation(
      LocationSettingsTranslationLocationReadTypeEnum.MeetingRoomDisclaimer,
    ),
  );
  const checkoutDisclaimer = computed(() =>
    getTranslation(
      LocationSettingsTranslationLocationReadTypeEnum.CheckoutDisclaimer,
    ),
  );

  const checkoutPopup = computed(() =>
    getTranslation(
      LocationSettingsTranslationLocationReadTypeEnum.ThynkDirectCheckoutPopup,
    ),
  );
  const bookPopup = computed(() =>
    getTranslation(
      LocationSettingsTranslationLocationReadTypeEnum.ThynkDirectPriceCalculationPopup,
    ),
  );

  const { partnerCurrency } = usePartnerStore();
  const locationCurrency = computed(() => {
    const actualCurrency = location.value?.currency ?? partnerCurrency.value;
    if (!actualCurrency || actualCurrency === 'null') {
      return 'EUR';
    }
    return actualCurrency;
  });

  const currency = computed(() => ({
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: locationCurrency.value,
  }));

  const eventTypes = computed(() => location.value?.bookingEventTypes ?? []);

  const allowCateringOnly = computed(
    () =>
      location.value?.locationSettings?.allowedOnlyCateringServices ?? false,
  );
  const allowFunctionSpacesOnly = computed(
    () => location.value?.locationSettings?.allowedOnlyFunctionSpaces ?? false,
  );
  const hallPreferenceEnabled = computed(
    () => location.value?.locationSettings?.hallPreferenceEnabled ?? true,
  );
  const isShoppingBasketVisible = computed(
    () => location.value?.locationSettings?.isShoppingBasketVisible ?? false,
  );
  return {
    location,
    locations,
    allLocations,
    locationsPromise,
    fetchLocations,
    meetingRoomDisclaimer,
    checkoutDisclaimer,
    eventTypes,
    currency,
    checkoutPopup,
    bookPopup,
    allowCateringOnly,
    allowFunctionSpacesOnly,
    hallPreferenceEnabled,
    isShoppingBasketVisible,
    locationById,
  };
}
